import { longitudeSpan } from './globeMode';
import type { ViewportPadding } from './googleSearchMapHandle';
import type { MapClusterPin, MapEnvelope } from './pins';

export const PROPERTY_FOCUS_MAPBOX_ZOOM = 10.1;

/** The latitude where the square Web Mercator world ends. */
export const MAX_MERCATOR_LATITUDE = 85.05112878;

/** Web Mercator y of a latitude, from 0 at the north edge of the world to 1 at the south. */
export const mercatorY = (latitude: number) => {
  const sin = Math.sin(
    (Math.max(-MAX_MERCATOR_LATITUDE, Math.min(MAX_MERCATOR_LATITUDE, latitude)) * Math.PI) / 180,
  );
  return 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
};

export const latitudeAtMercatorY = (y: number) =>
  (360 / Math.PI) * Math.atan(Math.exp((0.5 - y) * 2 * Math.PI)) - 90;

/** Pad each side of the server envelope so the revealed pins do not hug the edge. */
const ENVELOPE_PADDING = 1.25;
/** About 111 m. Stops a tap from diving to parcel level. */
const MIN_SPAN_DEGREES = 0.001;
/** Every tap must visibly zoom in, so the target never exceeds half the current span. */
const MAX_FRACTION_OF_CURRENT_SPAN = 0.5;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/** Longitude span shown at a whole zoom level on a 360 degree world. */
export const spanForZoom = (zoom: number): number => clamp(360 / 2 ** zoom, MIN_SPAN_DEGREES, 180);

const spanOf = (envelope: MapEnvelope) => ({
  lat: Math.abs(envelope.north - envelope.south),
  lon: longitudeSpan(envelope),
});

/**
 * The part of the map bounds that page chrome leaves visible. The cluster cap
 * measures against this so a tap still zooms in once fitBounds honours the
 * same padding.
 */
export const visibleEnvelope = (
  bounds: MapEnvelope,
  padding: ViewportPadding,
  size: { width: number; height: number },
): MapEnvelope => {
  if (size.width <= 0 || size.height <= 0) {
    return bounds;
  }
  const lonSpan = longitudeSpan(bounds);
  const latSpan = bounds.north - bounds.south;
  const east = bounds.east - lonSpan * ((padding.right ?? 0) / size.width);
  const west = bounds.west + lonSpan * ((padding.left ?? 0) / size.width);
  return {
    north: bounds.north - latSpan * ((padding.top ?? 0) / size.height),
    south: bounds.south + latSpan * ((padding.bottom ?? 0) / size.height),
    east: east < -180 ? east + 360 : east,
    west: west > 180 ? west - 360 : west,
  };
};

/**
 * Bounds to fit after a cluster tap. Mirrors the iOS BuyTabViewModel: center on
 * the centroid, size from the padded envelope or from the expansion zoom, then
 * cap at half the current viewport and floor at the minimum span.
 */
export const clusterExpansionBounds = (
  cluster: MapClusterPin,
  current: MapEnvelope,
): MapEnvelope => {
  const currentSpan = spanOf(current);
  let target: { lat: number; lon: number };
  if (cluster.envelope) {
    const envelopeSpan = spanOf(cluster.envelope);
    target = { lat: envelopeSpan.lat * ENVELOPE_PADDING, lon: envelopeSpan.lon * ENVELOPE_PADDING };
  } else {
    const lon = spanForZoom(cluster.expansionZoom);
    const ratio = currentSpan.lon > 0 ? currentSpan.lat / currentSpan.lon : 1;
    target = { lat: lon * ratio, lon };
  }
  const lat = clamp(
    target.lat,
    MIN_SPAN_DEGREES,
    currentSpan.lat > 0 ? currentSpan.lat * MAX_FRACTION_OF_CURRENT_SPAN : Infinity,
  );
  const lon = clamp(
    target.lon,
    MIN_SPAN_DEGREES,
    currentSpan.lon > 0 ? currentSpan.lon * MAX_FRACTION_OF_CURRENT_SPAN : Infinity,
  );
  return {
    north: cluster.latitude + lat / 2,
    south: cluster.latitude - lat / 2,
    east: cluster.longitude + lon / 2,
    west: cluster.longitude - lon / 2,
  };
};
